<template lang="pug">
div
    div(v-if="pData" class="p-3 md:bg-gray-lightest")
        script(v-if="pData" type="application/ld+json" v-html="getSchema()")
        Crumbs(:crumbs="pData.crumbs")
        div(class="lg:container mt-4")
            //- SIMILAR PRODUCTS
            CarouselProduct(v-if="isFeederPage || pData.isOutOfStock" productType='similar' :reserveSpace="true")
            div(class="grid md:grid-rows-[auto,1fr] md:grid-cols-[60%,1fr] xl:grid-cols-[auto,1fr] gap-x-6")
                div(class="order-1 py-3 md:row-span-2 xl:w-[860px]")
                    Gallery(:gallery="pData.gallery" :showThumbs="!$device.isMobile")
                        template(#footer)
                            div(
                                v-if="hasCustomerPhotos"
                                class="flex justify-between cursor-pointer mt-3 lg:mt-0"
                            )
                                div(@click="showCustomerPhotos" class="flex items-center")
                                    img(src="@/assets/social/instagram.svg" alt="" class="w-6 h-6 mr-2")
                                    | Customer Photos
                    //- Really this v-if shouldn't be needed as we should always have a brand
                    div(v-if="pData.brand && pData.brand.image" class="font-bold text-black text-center")
                        | AUTHORIZED DEALER:
                        img(:src="pData.brand.image.url" :alt="pData.brand.image.title" class="align-middle")
                div(class="order-first md:order-2 pt-3")
                    span(class="mb-3 mt-0 text-3xl text-black uppercase font-extrabold" v-if="isRealtruckBrand(pData.brand.name)")
                        | Realtruck
                        |
                    h1(
                        data-testid="productTitle"
                        v-html="pData.name"
                        class="mb-3 mt-0 text-3xl inline"
                    )
                    br
                    Reviews(:data="pData")
                    Pricing(:data="pData")
                div(class="order-3 pt-3")
                    ProductInfo
                    PromoImages(v-if="pData.promoImages")
            div(v-if="pControls.groups.length > 0" ref='groups')
                Group(
                    v-for="(group,index) in pControls.groups"
                    :arrayIndex="index"
                    :key="group.title"
                    :group="group"
                    :qtySelector="pData.qtySelector"
                )

            div(v-else-if="pControls.integration" ref='groups')
                ThirdParty(:integration="pControls.integration")

            //- RELATED/RECOMMENDED PRODUCTS
            CarouselProduct(v-if="hasRelatedProducts" productType="related")
            CarouselProduct(v-else productType="recommended")

    div(v-if="pData" class="p-3 md:bg-gray-lightest")
        div(class="lg:container mt-4")

            ExtraInformation(:hasCustomerPhotos="hasCustomerPhotos")

            //- RECOMMENDED/SIMILAR PRODUCTS
            CarouselProduct(v-if="hasRelatedProducts" productType="recommended")
            CarouselProduct(id="carousel-similar-bottom" v-if="!isFeederPage && !pData.isOutOfStock" productType='similar')

            //- RECENT PRODUCTS
            CarouselProduct(productType='recent')

    CartPopup(v-if="activeOverlay == 'mc'")
</template>

<script>
import mitt from 'mitt'
import IsRealtruckBrandMixin from '@/helpers/isRealtruckBrandMixin'
import Gallery from '@/components/structuredContent/_components/galleryCarousel'
import Crumbs from '@/components/crumbs'
import CarouselProduct from '@/components/carousel/product/'
import Reviews from './_components/reviews.vue'
import Group from './_components/group'
import PromoImages from './_components/promoImages'
import ProductInfo from './_components/productInfo'
import ThirdParty from './_components/thirdParty'
import ExtraInformation from './_components/extraInfo'
import alertHelper from './_helpers/alert'
import Pricing from './_components/pricing'
const CartPopup = () => import(/* webpackChunkName: "cart-popup" */ '@/components/cart/cartPopup')

const emitter = mitt()

export default {
    name: 'Product',
    components: {
        Gallery,
        Group,
        Crumbs,
        PromoImages,
        ProductInfo,
        ThirdParty,
        ExtraInformation,
        CartPopup,
        CarouselProduct,
        Reviews,
        Pricing,
    },
    mixins: [IsRealtruckBrandMixin],
    provide() {
        return {
            $pageBus: {
                $on: emitter.on,
                $off: emitter.off,
                $emit: emitter.emit,
            },
            ...alertHelper,
        }
    },
    beforeRouteUpdate(to, from, next) {
        // you selected your YMMBBE, hit back, then hit forward :(  Now the controls are wonkey and your session is missing fitment items not found in the url
        const isFittedHistory =
            from.name === 'product' && to.name === 'product-mmy' && !to.params.noUpdateScroll

        // on sku page and we just updated the sku in the url, do nothing!
        if (to.params.doNotReload) {
            delete to.params.doNotReload

            return next()
        }
        // you updated fitment from the global modal or clicked Verify Vehicle Fitment on a sku page
        if (
            isFittedHistory ||
            to.params.forceReload ||
            (to.params.skuSlug !== from.params.skuSlug &&
                to.params.productLine === from.params.productLine)
        ) {
            // call next first so the current data is caught up
            this.$store.dispatch('product/initPage', { params: to.params }).then(() => {
                next()
                // Just in case they had a ?query param we need to wait an extra tick just to be
                // sure the data fully updated.
                this.$nextTick(() => {
                    this.initControls()
                })
                delete to.params.forceReload
            })
            return
        }

        if (
            to.query.mod ||
            from.query.mod || // you triggered a modal, ignore
            to.params.skuSlug !== from.params.skuSlug || // you came to/from a pline > sku page, fetch got you bro
            to.params.productLine !== from.params.productLine // you came to/from a pline > diff pline page, fetch got you bro
        ) {
            next()
            return
        }

        // you updated fitment (Year/Make/Model) within the OC drills (not BBE)
        next()
    },
    async asyncData({ store, params, query }) {
        if (query.gclid) store.commit('product/setFromAd', true)
        else if (
            store.state.product.fromAd &&
            store.state.product.productLineSlug !== params.productLine
        )
            store.commit('product/setFromAd', false)

        // when going from a sku page with ?anything=blah fetch will trigger.  This is cleaned up in beforeRouteUpdate.  Ug..
        if (params.doNotReload) return

        await store.dispatch('product/initPage', { params })
    },
    data() {
        return {
            hasCustomerPhotos: false,
        }
    },
    head() {
        const variables = {
            BRAND: this.pData.brand.name,
        }

        let templateNamePrefix = null

        if (this.isProductPage) {
            variables.PRODUCT = this.pData.name
            variables.SKU = this.$sitewideData.config.showMpn ? this.pData.mpn : this.pData.sku
            templateNamePrefix = 'product'
        } else {
            variables.PRODUCT_LINE = this.pData.name
            variables.FITMENT = this.fitmentFormatted
            templateNamePrefix = 'productLine'
        }

        const template = this.$sitewideData.getBaseMetaTemplates(templateNamePrefix)
        const metas = this.$metaHelpers.generateMetas(
            { ...template, ...this.pData.meta },
            this.$route,
            variables
        )

        // No Index, yes it's funny we support metas at all
        if (!this.isProductPage && this.fitment.make) metas.setIsNoIndex()

        // Social Metas
        metas.setSocialUrl(this.$route.path)

        const imageUrl = this.$getImageUrl(
            this.pData.gallery[0].key,
            this.pData.gallery[0].filename,
            800,
            600,
            80,
            'fff'
        )
        metas.setSocialImage(`https://${this.$sitewideData.domain}${imageUrl}`)

        return metas.format()
    },
    computed: {
        activeOverlay() {
            return this.$overlay.state.activeOverlay
        },
        pData() {
            return this.$store.state.product.data
        },
        pControls() {
            return this.$store.state.product.controls
        },
        fitment() {
            return this.$store.state.fitment.data
        },
        isProductPage() {
            return this.$route.meta.isProductPage
        },
        isFeederPage() {
            return this.$store.state.product.fromAd
        },
        fitmentFormatted() {
            return this.$store.getters['fitment/fitmentFormatted']
        },
        hasRelatedProducts() {
            return !!this.$store.state.recommendations.related
        },
        hasInteraction() {
            return this.pControls.hasInteraction
        },
    },
    watch: {
        'pData.disableBestPrice'(newValue) {
            this.$uiEvents.$emit('hidingPromoImages', newValue)
        },
        // update the product page url if you manually drilled to a diff sku
        'pData.skuSlug'(newValue) {
            if (
                !this.isProductPage ||
                newValue === null ||
                !this.hasInteraction ||
                this.$route.params.skuSlug === newValue
            )
                return

            this.$push.replace({
                name: 'sku',
                params: {
                    productLine: this.$route.params.productLine,
                    skuSlug: newValue,
                    doNotReload: true,
                    noUpdateScroll: true,
                },
            })
        },
    },
    // this doesn't seem to be called any way I try on 404's, so that's good
    async mounted() {
        this.initControls()

        this.$uiEvents.$on('chooseOptionsClicked', () => {
            this.$scrollTo(this.$refs.groups)
        })

        if (this.$yotpo.enabled)
            this.hasCustomerPhotos = await this.$yotpo.getCustomerPhotos(this.pData.yotpoId)

        this.$lux.label('Product Line Page')
        this.$lux.pageLoading(false)
    },
    beforeDestroy() {
        this.$uiEvents.$emit('hidingPromoImages', false)
        this.$uiEvents.$off('chooseOptionsClicked')
    },
    methods: {
        getSchema() {
            const obj = {
                '@context': 'http://schema.org',
                '@type': 'Product',
                name: this.pData.name,
            }

            if (this.pData.gallery && this.pData.gallery[0])
                obj.image = this.$getImageUrl(
                    this.pData.gallery[0].key,
                    this.pData.gallery[0].filename
                )

            if (this.pData.totalReviews > 0) {
                obj.aggregateRating = {
                    '@type': 'AggregateRating',
                    ratingValue: this.pData.averageRating,
                    reviewCount: this.pData.totalReviews,
                }
            }

            if (this.pData.brand && this.pData.brand.name) {
                obj.brand = {
                    '@type': 'brand',
                    name: this.pData.brand.name,
                    // "logo": ?
                }
            }

            if (!this.isProductPage) {
                if (!this.pData.hasMAPViolation) {
                    if (this.pData.actualPrice?.min !== this.pData.actualPrice?.max)
                        obj.offers = {
                            '@type': 'AggregateOffer',
                            lowPrice: this.pData.actualPrice.min / 100,
                            highPrice: this.pData.actualPrice.max / 100,
                            priceCurrency: 'USD',
                            itemCondition: 'NewCondition',
                        }
                    else if (this.pData.actualPrice) {
                        obj.offers = {
                            '@type': 'Offer',
                            price: this.pData.actualPrice.min / 100,
                            priceCurrency: 'USD',
                            itemCondition: 'NewCondition',
                        }
                    }
                }
            } else {
                if (this.pData.actualPrice) {
                    obj.offers = {
                        '@type': 'Offer',
                        price: this.pData.actualPrice.min / 100,
                        priceCurrency: 'USD',
                        itemCondition: 'NewCondition',
                    }
                }
                obj.mpn = this.pData.mpn || this.pData.sku
                obj.sku = this.pData.sku
                obj.url = `https://${this.$sitewideData.domain}${this.$route.path}`
            }

            if (obj.offers) {
                obj.offers.availability = this.pData.isOutOfStock
                    ? 'http://schema.org/OutOfStock'
                    : 'http://schema.org/InStock'
            }
            return JSON.stringify(obj)
        },
        initControls() {
            this.$store.dispatch('product/initProductLineControls')
        },
        showCustomerPhotos() {
            if (this.$device.isDesktop) {
                const element = document.getElementById('scrollTarget-Customer Photos')
                this.$scrollTo(element)
            } else {
                this.$overlay.show('Customer Photos')
            }
        },
    },
}
</script>
